<template>
  <!-- 신규 수리접수 등록 HITL 카드 — 사용자가 [등록]을 눌러야 실제 커밋된다.
       상태(committing/done)는 부모 메시지 객체가 보유, 본 블록은 commit/cancel emit만. -->
  <div class="aw-draft">
    <div class="aw-draft-title">{{ $t('assistant.draftTitle') }}</div>
    <!-- 초안 내용은 칩 선택을 즉시 반영(displayPreview) — [등록] 후에도 선택 반영본이 남아
         실제 등록된 값과 카드 표시가 일치한다 (2026-07-29). -->
    <dl class="aw-draft-list">
      <template v-for="(val, key) in displayPreview" :key="key">
        <dt>{{ key }}</dt>
        <dd>{{ val }}</dd>
      </template>
    </dl>
    <div v-if="displayMissing.length" class="aw-draft-warn">
      {{ $t('assistant.draftMissing', { fields: displayMissing.join(', ') }) }}
    </div>
    <!-- 카드에서 채울 항목 한 목록(fieldRows) — 후보 선택과 직접 입력을 순서대로 배치한다.
         칩 그룹(모델/증상/사용자그룹/확인사항): 클릭은 로컬 토글만 — 선택값은 commit 페이로드로 전달.
           '증상' 선택 시 해당 유형의 확인사항 후보가 증상 바로 아래 표시된다.
         드롭다운(select_fields=지역): 전량 후보 로컬 단일 선택. 주소·상세주소는 그 바로 아래(addressBlock).
         코드 없는 그룹(제품모델 등): 기존처럼 클릭 즉시 재작성 발화(query) 전송. -->
    <div v-if="!done && fieldRows.length" class="aw-draft-candidates">
      <div class="aw-draft-cand-hint">{{ $t(fieldsHintKey) }}</div>
      <div v-for="row in fieldRows" :key="row.key" class="aw-draft-cand-group">
        <span class="aw-draft-cand-field">{{ row.label }}</span>
        <!-- kind 'optional' = 위치 정보 묶음으로 옮겨 온 선택 입력(확정 주소·상세주소) — 값은 optionalInputs -->
        <template v-if="row.kind === 'input' || row.kind === 'optional'">
          <input
            class="aw-draft-input"
            :type="row.key === 'customer_phone' ? 'tel' : 'text'"
            :aria-label="row.label"
            :placeholder="row.label"
            :maxlength="inputMaxlength(row.key)"
            :disabled="committing"
            :value="row.kind === 'optional' ? optionalInputVal(row.opt) : (inputs[row.key] || '')"
            @input="row.kind === 'optional' ? onOptionalInput(row.key, $event.target.value) : onInput(row.key, $event.target.value)"
          >
          <!-- daum 우편번호 팝업 버튼은 반드시 '주소' 행에 붙인다 — '지역' 그룹이 없는 초안이
               정상적으로 발생하므로 드롭다운 옆에 붙이면 그 케이스를 놓친다 (AA-137). -->
          <button
            v-if="row.search === 'postcode'"
            type="button"
            class="aw-draft-cand-chip"
            :disabled="committing"
            @click="onAddressSearch"
          >
            {{ $t('assistant.addressSearch') }}
          </button>
        </template>
        <select
          v-else-if="isSelectField(row.field)"
          class="aw-draft-cand-select"
          :aria-label="row.field"
          :disabled="committing"
          :value="selected[row.field] !== undefined ? selected[row.field] : ''"
          @change="onSelectChange(row.field, $event.target.value)"
        >
          <option value="">{{ $t('assistant.draftSelectPlaceholder') }}</option>
          <option v-for="(c, i) in row.items" :key="row.key + i" :value="i">{{ c.label }}</option>
        </select>
        <template v-else>
          <button
            v-for="(c, i) in row.items"
            :key="row.key + i"
            type="button"
            class="aw-draft-cand-chip"
            :class="{ 'aw-draft-cand-chip--selected': isSelected(row.field, i) }"
            :disabled="committing"
            @click="onChipClick(row.field, i, c)"
          >
            {{ c.label }}
          </button>
        </template>
      </div>
    </div>
    <!-- optional 필드(고객구분/긴급/강성요청여부·내용) — 기본값이 있어 선택 안 해도 [등록] 가능.
         key 는 commit 페이로드/BE CommitRequest 계약과 고정 동기화. 위치 정보 묶음으로 옮긴 주소·상세주소는 빠진다. -->
    <div v-if="!done && optionalRows.length" class="aw-draft-optional">
      <div v-for="f in optionalRows" :key="f.key" class="aw-draft-cand-group">
        <span class="aw-draft-cand-field">{{ f.label }}</span>
        <select
          v-if="f.kind === 'select'"
          class="aw-draft-cand-select"
          :aria-label="f.label"
          :disabled="committing"
          :value="optionalSelectCode(f)"
          @change="onOptionalSelect(f.key, $event.target.value)"
        >
          <option v-for="it in f.items" :key="f.key + it.code" :value="it.code">{{ it.label }}</option>
        </select>
        <template v-else>
          <input
            class="aw-draft-input"
            type="text"
            :aria-label="f.label"
            :placeholder="f.label"
            :maxlength="inputMaxlength(f.key)"
            :disabled="committing"
            :value="optionalInputVal(f)"
            @input="onOptionalInput(f.key, $event.target.value)"
          >
          <button
            v-if="f.search === 'postcode'"
            type="button"
            class="aw-draft-cand-chip"
            :disabled="committing"
            @click="onAddressSearch"
          >
            {{ $t('assistant.addressSearch') }}
          </button>
        </template>
      </div>
    </div>
    <!-- 주소 검색 실패 안내 — 지역을 확정하지 못하면 주소·우편번호도 반영하지 않는다(AA-137) -->
    <div v-if="!done && postcodeError" class="aw-draft-warn">{{ postcodeError }}</div>
    <div v-if="committing && !done" class="aw-draft-status">{{ $t('assistant.committing') }}</div>
    <div v-if="!done" class="aw-draft-actions">
      <button
        type="button"
        class="aw-draft-btn aw-draft-commit"
        :disabled="registerDisabled"
        @click="$emit('commit', commitPayload)"
      >
        {{ $t('assistant.register') }}
      </button>
      <button
        type="button"
        class="aw-draft-btn aw-draft-cancel"
        :disabled="committing"
        @click="$emit('cancel')"
      >
        {{ $t('assistant.cancel') }}
      </button>
    </div>
  </div>
</template>

<script>
import { loadAreaList, openPostcodeSearch } from '@/common/postcodeSearch'

/** 그룹 표시명(공백 제거) → commit 페이로드 키 — BE CommitRequest 계약과 고정 동기화 */
const GROUP_COMMIT_KEYS = { 모델: 'product_id', 증상: 'main_type_code', 사용자그룹: 'user_group_id', 지역: 'area_code' }

/** 그룹 표시명(공백 제거) → preview 행 라벨 — ai-service prepare 의 preview 키와 고정 동기화
    (모델 후보 선택은 '제품군'이 아닌 개별 '모델명' 행에 반영 — csback.py preview 계약) */
const GROUP_PREVIEW_KEYS = { 모델: '모델명', 증상: '증상', 확인사항: '확인사항', 사용자그룹: '사용자 그룹', 지역: '지역' }

/** 직접 입력 필드 key → preview 행 라벨 — 전화번호만 preview 에서 '전화'로 축약 표기 */
const INPUT_PREVIEW_KEYS = { customer_name: '고객명', customer_phone: '전화', address: '주소' }

/** optional 필드 key → preview 행 라벨 — ai-service prepare 의 preview 키와 동기화 */
const OPTIONAL_PREVIEW_KEYS = { customer_type: '고객구분', emergency: '긴급', strong_request_yn: '강성요청', strong_request_content: '강성요청내용', address: '주소', detail_address: '상세주소' }

/** 자유 텍스트 입력 상한 — ai-service commit 의 길이 400 을 카드에서 도달 불가하게 만든다 (AA-137) */
const INPUT_MAXLENGTH = { address: 200, detail_address: 255 }

/**
 * @component DraftConfirmBlock
 * @description HITL 접수 등록 확인 카드. data={preview, missing, draft_id, confirmation_token,
 *   candidates?, multi_fields?, check_candidates_by_type?, input_fields?, select_fields?}.
 *   2026-07-31 카드 내 완결 입력: 미입력 자유입력 필드(input_fields — 고객명/전화번호/주소)는
 *   카드 안 텍스트 입력으로, 지역 전량 후보(select_fields)는 드롭다운으로 채워 commit 페이로드
 *   ({customer_name, customer_phone, address, area_code})로 전달 — 발화 재경유 없이 등록 완결.
 *   렌더 순서(fieldRows)는 후보 그룹과 입력을 한 목록으로 합치되 종속 항목을 인접 배치한다 —
 *   합성 확인사항은 증상 바로 아래, 주소 입력은 지역 드롭다운 바로 아래.
 *   2026-07-29 개편(후보 일괄 로컬 선택 — 칩 1개 클릭마다 초안이 재생성되던 UX 폐기):
 *   - 코드(code) 있는 그룹: 로컬 선택(단일 그룹=라디오 토글, multi_fields 그룹=다건 토글).
 *     '증상' 선택 시 data.check_candidates_by_type[유형코드]로 확인사항 후보가 즉시 전환되고,
 *     증상 변경/해제 시 확인사항 선택은 리셋된다.
 *   - [등록]: 코드 그룹 전부 선택(확인사항은 후보 존재 시 1개 이상) + 코드 외 missing 없음일 때
 *     활성화. 클릭 시 선택값({product_id, main_type_code, user_group_id, sub_type_codes})을
 *     commit 페이로드로 emit — BE 가 draft.allowed_selections 화이트리스트로 검증 후 반영.
 *   - 초안 내용 동기 표시(2026-07-29): 칩 선택 즉시 preview 행(모델명/증상/확인사항/사용자 그룹)에
 *     선택 라벨을 겹쳐 보여주고, 충족된 missing 경고 항목은 감춘다 — [등록] 후(done)에도
 *     선택 반영본이 남아 실제 등록 값과 카드 표시가 일치.
 *   - 코드 없는 그룹(지역 등): 기존 즉시 refine(query) 재작성 경로 유지.
 */
export default {
  name: 'DraftConfirmBlock',
  props: {
    data: { type: Object, default: () => ({}) },
    committing: { type: Boolean, default: false },
    done: { type: Boolean, default: false }
  },
  emits: ['commit', 'cancel', 'refine'],
  data() {
    return {
      selected: {}, // {필드: 칩 index(단일) | 칩 index[](multi_fields 그룹)}
      inputs: {}, // {input_fields key: 입력 텍스트} — 고객명/전화번호/주소 직접 입력 (2026-07-31)
      optionalSelected: {}, // {optional select key: 선택 code} — 고객구분/긴급/강성여부
      optionalInputs: {}, // {optional input key: 입력 텍스트} — 강성요청내용
      // 주소 검색(daum 우편번호) 결과 (AA-137)
      postalCode: '', // 선택한 주소의 우편번호 — commit 페이로드 postal_code
      postcodeArea: null, // 해석된 지역 {areaMainCode, areaSubCode, areaMainName, areaSubName, isNewDistrict}
      pendingAreaOption: null, // 마스터 미등재 시군구의 임시 드롭다운 옵션 — 누적이 아니라 교체
      postcodeError: '' // 지역 해석 실패 안내 문구
    }
  },
  created() {
    // 지역 마스터 예열 — 팝업은 동기 함수여야 해서(팝업 차단기) oncomplete 안에서 await 한다.
    // loadAreaList 는 reject 하지 않고 실패 시 캐시도 남기지 않으므로 여기서 결과를 볼 필요가 없다.
    loadAreaList()
  },
  computed: {
    hasCandidates() {
      return Object.keys(this.displayCandidates).length > 0
    },
    multiFieldSet() {
      const mf = this.data.multi_fields
      return Array.isArray(mf) ? mf : []
    },
    /** 표시할 후보 그룹 — '증상' 로컬 선택 시 해당 유형의 확인사항 그룹을 동적 합성.
        합성된 확인사항 그룹은 증상 그룹 바로 아래에 삽입한다(키 순서 재구성, 2026-07-31). */
    displayCandidates() {
      const base = { ...(this.data.candidates || {}) }
      // 주소 검색으로 얻은 마스터 미등재 시군구를 지역 드롭다운에 임시 옵션으로 덧붙인다 (AA-137).
      // ⚠️ base 는 props 의 얕은 복사라 push 하면 computed 가 자기 의존성(props)을 변형한다
      //    (재계산 루프 / 임시 옵션 영구 잔류 / 두 번 검색 시 누적) — 반드시 새 배열로 만든다.
      if (this.pendingAreaOption) base['지역'] = [...(base['지역'] || []), this.pendingAreaOption]
      const byType = this.data.check_candidates_by_type
      if (byType && !base['확인사항']) {
        const mtIdx = this.selected['증상']
        const mtCode = mtIdx !== undefined ? this.chipCode((base['증상'] || [])[mtIdx]) : ''
        const checks = mtCode && byType[mtCode]
        if (checks && checks.length) {
          const out = {}
          for (const [field, items] of Object.entries(base)) {
            out[field] = items
            if (field === '증상') out['확인사항'] = checks.map(c => ({ label: c.label, code: c.code }))
          }
          return out
        }
      }
      return base
    },
    /** 카드 내 직접 입력 필드(고객명/전화번호/주소) — prepare 가 미입력분만 내려준다 (2026-07-31) */
    inputFields() {
      const list = Array.isArray(this.data.input_fields) ? this.data.input_fields : []
      return list.filter(f => f && f.key && f.label)
    },
    /** optional 필드(고객구분/긴급/강성요청여부·내용) — 기본값이 있어 [등록] 게이트에 포함되지 않는다 */
    optionalFields() {
      const list = Array.isArray(this.data.optional_fields) ? this.data.optional_fields : []
      return list.filter(f => f && f.key && f.label && (f.kind === 'input' || (Array.isArray(f.items) && f.items.length)))
    },
    /** 위치 정보 묶음 — 지역 드롭다운 → 주소 → 상세주소를 연달아 보여 준다.
        주소는 비어 있으면 필수 입력(input_fields), 확정이면 선택 입력(optional_fields)으로 오고 상세주소는 늘 선택
        입력이라, 구역대로 그리면 상세주소가 카드 맨 아래로 떨어진다. 화면 배치만 바꾸고 값 바인딩·[등록] 게이트는
        원래 구역 그대로다(kind 'optional' 은 optionalInputs 에 쓴다). 지역 드롭다운도 필수 주소 입력도 없으면
        (지역·주소 모두 확정) 선택 입력 구역에서 이미 주소·상세주소가 연달아 나오므로 옮기지 않는다. */
    addressBlock() {
      const addrInput = this.inputFields.find(f => f.key === 'address')
      const atArea = Object.prototype.hasOwnProperty.call(this.displayCandidates, '지역')
      if (!atArea && !addrInput) return null
      const toOptional = f => ({ kind: 'optional', key: f.key, label: f.label, search: f.search, opt: f })
      const addrOpt = this.optionalFields.find(f => f.key === 'address')
      const detail = this.optionalFields.find(f => f.key === 'detail_address')
      const rows = []
      if (addrInput) rows.push({ kind: 'input', key: addrInput.key, label: addrInput.label, search: addrInput.search })
      else if (addrOpt) rows.push(toOptional(addrOpt))
      if (detail) rows.push(toOptional(detail))
      return { atArea, rows }
    },
    /** 카드에서 채울 항목의 렌더 순서 — 후보 그룹(칩/드롭다운)과 직접 입력을 한 목록으로 합친다.
        위치 정보 묶음(주소·상세주소)은 지역 드롭다운 바로 아래, 드롭다운이 없으면 필수 주소 입력 자리에 둔다.
        나머지 입력은 후보 그룹 뒤. */
    fieldRows() {
      const toInput = f => ({ kind: 'input', key: f.key, label: f.label, search: f.search })
      const block = this.addressBlock
      const rows = []
      for (const [field, items] of Object.entries(this.displayCandidates)) {
        rows.push({ kind: 'group', key: field, field, label: field, items })
        if (block && block.atArea && field === '지역') rows.push(...block.rows)
      }
      for (const f of this.inputFields) {
        if (block && f.key === 'address') {
          if (!block.atArea) rows.push(...block.rows)
          continue
        }
        rows.push(toInput(f))
      }
      return rows
    },
    /** 선택 입력 구역에 남는 필드 — 위치 정보 묶음으로 옮긴 주소·상세주소는 뺀다 */
    optionalRows() {
      const block = this.addressBlock
      if (!block) return this.optionalFields
      const moved = new Set(block.rows.filter(r => r.kind === 'optional').map(r => r.key))
      return this.optionalFields.filter(f => !moved.has(f.key))
    },
    /** 안내 문구 — 후보 그룹이 없으면(입력만) 입력 안내, 있으면 기존 후보 선택 안내 */
    fieldsHintKey() {
      if (!this.hasCandidates) return 'assistant.draftInputHint'
      return this.hasCodeGroups ? 'assistant.candidateHintMulti' : 'assistant.candidateHint'
    },
    /** 값이 입력된 직접 입력 필드 — {commit 페이로드 key: trim 값} */
    filledInputs() {
      const out = {}
      for (const f of this.inputFields) {
        const v = String(this.inputs[f.key] || '').trim()
        if (v) out[f.key] = v
      }
      return out
    },
    hasCodeGroups() {
      return Object.keys(this.displayCandidates).some(f => this.groupHasCodes(f))
    },
    /** 초안에 지역 드롭다운이 있는지 — 지역이 이미 확정돼 그룹이 없는 초안이 정상적으로 발생한다 */
    hasAreaGroup() {
      return Object.prototype.hasOwnProperty.call(this.data.candidates || {}, '지역')
    },
    /** 주소 검색으로 해석된 지역의 드롭다운 코드 형식(`MAIN|SUB`) */
    postcodeAreaCode() {
      const a = this.postcodeArea
      return a ? `${a.areaMainCode}|${a.areaSubCode}` : ''
    },
    /** 코드 그룹별 선택 라벨 — {정규화 그룹명: 라벨 문자열(다건은 ', ' 연결)} */
    selectedLabels() {
      const norm = s => String(s).replace(/\s+/g, '')
      const out = {}
      for (const [field, items] of Object.entries(this.displayCandidates)) {
        if (!this.groupHasCodes(field)) continue
        if (this.isMultiField(field)) {
          const labels = (this.selected[field] || []).map(i => items[i] && items[i].label).filter(Boolean)
          if (labels.length) out[norm(field)] = labels.join(', ')
          continue
        }
        const i = this.selected[field]
        if (i !== undefined && items[i]) out[norm(field)] = items[i].label
      }
      return out
    },
    /** 초안 표시 내용 — preview 위에 칩 선택 라벨·직접 입력값을 겹쳐 실제 등록될 값과 일치시킨다 */
    displayPreview() {
      const out = { ...(this.data.preview || {}) }
      for (const [group, label] of Object.entries(this.selectedLabels)) {
        const key = GROUP_PREVIEW_KEYS[group]
        if (key) out[key] = label
      }
      for (const f of this.inputFields) {
        const v = this.filledInputs[f.key]
        const key = INPUT_PREVIEW_KEYS[f.key]
        if (v && key) out[key] = v
      }
      // optional 선택/입력을 preview 행에 겹쳐 실제 등록될 값과 일치시킨다
      for (const f of this.optionalFields) {
        const pk = OPTIONAL_PREVIEW_KEYS[f.key]
        if (!pk) continue
        if (f.kind === 'input') {
          const v = this.optionalInputVal(f).trim()
          if (v) out[pk] = v
        } else {
          const it = (f.items || []).find(x => String(x.code) === String(this.optionalSelectCode(f)))
          if (it) out[pk] = it.label
        }
      }
      // 주소 검색 결과 (AA-137) — '지역' 그룹이 없는 초안은 선택 라벨이 없으므로 해석 결과를 직접 표시한다
      // (그룹이 있으면 selectedLabels 가 이미 선택 옵션 라벨을 얹는다). 카드 표시와 전송값은 항상 일치해야 한다.
      if (this.postcodeArea && !this.hasAreaGroup) {
        out['지역'] = `${this.postcodeArea.areaMainName} ${this.postcodeArea.areaSubName}`
      }
      if (this.postalCode) out['우편번호'] = this.postalCode
      return out
    },
    /** missing 경고 표시분 — 칩 선택·직접 입력으로 이미 충족된 항목은 제외 */
    displayMissing() {
      const norm = s => String(s).replace(/\s+/g, '')
      const satisfied = Object.keys(this.selectedLabels)
      for (const f of this.inputFields) {
        if (this.filledInputs[f.key]) satisfied.push(norm(f.label))
      }
      return (this.data.missing || []).filter(m => !satisfied.some(f => norm(m).startsWith(f)))
    },
    /** commit 페이로드 — 선택된 코드 그룹 값 + 직접 입력값 (BE 화이트리스트/빈슬롯 검증 전제) */
    commitPayload() {
      const out = {}
      for (const [field, items] of Object.entries(this.displayCandidates)) {
        if (this.isMultiField(field)) {
          const codes = (this.selected[field] || [])
            .map(i => this.chipCode(items[i])).filter(Boolean)
          if (codes.length) out.sub_type_codes = codes
          continue
        }
        const i = this.selected[field]
        if (i === undefined) continue
        const key = GROUP_COMMIT_KEYS[String(field).replace(/\s+/g, '')]
        const code = this.chipCode(items[i])
        if (key && code) out[key] = code
      }
      Object.assign(out, this.filledInputs)
      // optional 필드 — 기본값 포함 항상 유효값 전송(BE 화이트리스트/범위 검증). 강성내용은 비면 생략.
      for (const f of this.optionalFields) {
        if (f.kind === 'input') {
          const v = this.optionalInputVal(f).trim()
          if (v) out[f.key] = v
        } else {
          const code = this.optionalSelectCode(f)
          if (code) out[f.key] = code
        }
      }
      // 주소 검색 결과 (AA-137) — 전송값의 단일 출처는 드롭다운이다.
      // '지역' 그룹이 있으면 위 루프의 selected['지역'] 만 읽고 postcodeArea 로 덮지 않는다.
      // 덮으면 주소 검색 한 번에 드롭다운이 죽은 컨트롤이 되고, 사용자가 눈으로 되돌린 신규 구가
      // 마스터에 INSERT 된다. 시군구명(마스터 신설 통로)은 마스터 미등재 임시 옵션이 선택됐을 때만 싣는다.
      if (this.hasAreaGroup) {
        if (this.pendingAreaOption && out.area_code === this.pendingAreaOption.code) {
          out.area_sub_name = this.pendingAreaOption.areaSubName
        }
      } else if (this.postcodeArea) {
        // '지역' 그룹이 없는 초안 — 되돌릴 드롭다운이 없으므로 해석 결과를 직접 싣는다
        out.area_code = this.postcodeAreaCode
        if (this.postcodeArea.isNewDistrict) out.area_sub_name = this.postcodeArea.areaSubName
      }
      if (this.postalCode) out.postal_code = this.postalCode
      return out
    },
    /** [등록] 비활성 — 코드 그룹 미선택/직접 입력 미기입 존재 또는 카드에서 채울 수 없는 missing 존재. */
    registerDisabled() {
      if (this.committing) return true
      const groups = this.displayCandidates
      const norm = s => String(s).replace(/\s+/g, '')
      // 카드 안에서 충족 가능한 missing 라벨 — 코드 그룹 + 확인사항(증상 선택 전 동적 미등장 포함)
      // + 직접 입력 필드(고객명/전화번호/주소)
      const selectable = Object.keys(groups).filter(f => this.groupHasCodes(f)).map(norm)
      selectable.push('확인사항')
      for (const f of this.inputFields) selectable.push(norm(f.label))
      const others = (this.data.missing || [])
        .filter(m => !selectable.some(f => norm(m).startsWith(f)))
      if (others.length > 0) return true
      // 직접 입력 필드 전부 기입돼야 활성 (미입력분만 내려오므로 전량 필수)
      for (const f of this.inputFields) {
        if (!this.filledInputs[f.key]) return true
      }
      // 코드 그룹 전부 선택 완료 검사 (확인사항은 다건 1+)
      for (const [field, items] of Object.entries(groups)) {
        if (!this.groupHasCodes(field)) continue
        if (this.isMultiField(field)) {
          if (!(this.selected[field] || []).length) return true
        } else if (this.selected[field] === undefined || !items[this.selected[field]]) {
          return true
        }
      }
      // 확인사항 후보가 아직 등장 전(증상 미선택)인데 missing 에 요구되어 있으면 비활성
      const needsChecks = (this.data.missing || []).some(m => norm(m).startsWith('확인사항'))
      if (needsChecks && !groups['확인사항']) return true
      return false
    }
  },
  methods: {
    chipCode(c) {
      if (!c) return ''
      if (c.code) return String(c.code)
      // 구계약 칩 폴백 — clause 의 "(sub_type_code X)" 류 파싱
      const m = /(?:sub_type_code|main_type_code|product_id|user_group_id)\s+([A-Za-z0-9_-]+)/
        .exec(c.clause || c.query || '')
      return m ? m[1] : ''
    },
    groupHasCodes(field) {
      const items = this.displayCandidates[field] || []
      return items.some(c => !!this.chipCode(c))
    },
    isMultiField(field) {
      return this.multiFieldSet.includes(field)
    },
    /** select_fields 계약 그룹(지역) — 전량 후보라 칩 대신 드롭다운 렌더 (2026-07-31) */
    isSelectField(field) {
      const sf = this.data.select_fields
      return Array.isArray(sf) && sf.includes(field)
    },
    onSelectChange(field, val) {
      if (val === '') delete this.selected[field]
      else this.selected[field] = Number(val)
      // 사용자가 지역을 직접 고르면 주소 검색 연동을 끊는다 — 전송값의 단일 출처는 드롭다운이다 (AA-137)
      if (String(field).replace(/\s+/g, '') === '지역') this.postcodeArea = null
    },
    onInput(key, val) {
      this.inputs[key] = val
    },
    /** 자유 텍스트 입력 상한 — 상한이 없는 필드는 속성 자체를 붙이지 않는다 */
    inputMaxlength(key) {
      return INPUT_MAXLENGTH[key]
    },
    /**
     * [주소 검색] 클릭 — daum 우편번호 팝업을 연다.
     * ⚠️ await 을 붙이면 사용자 제스처 태스크를 벗어나 팝업 차단기에 막힌다(동기 호출 유지).
     */
    onAddressSearch() {
      const opened = openPostcodeSearch(res => this.onPostcodeResolved(res))
      // daum 스크립트 미로드(CDN 차단 등) — 버튼이 조용히 죽지 않도록 직접 입력을 안내한다
      if (!opened) this.postcodeError = this.$t('assistant.addressSearchUnavailable')
    },
    /**
     * 주소 선택 완료 — **지역 판정을 먼저 한다.**
     * ⚠️ 주소·우편번호를 먼저 반영하면 지역 해석 실패 시 "주소는 서초구인데 지역은 강남구로 접수"되는
     *   무음 오등록이 생긴다(이 작업이 막으려던 바로 그 실패). 전부 반영하거나 전부 안 하거나 둘 중 하나여야
     *   카드 표시와 전송값이 항상 일치한다.
     * @param {{postalCode:string, address:string, area:object, isNewDistrict:boolean}} res
     */
    onPostcodeResolved(res) {
      if (!res.area.areaSubCode) {
        // 마스터에도 없고 신규 구성도 불가 — 지역 그룹이 없는 초안에서는 '직접 선택'이 불가능한 지시다
        this.postcodeError = this.$t(this.hasAreaGroup
          ? 'assistant.addressAreaFailed'
          : 'assistant.addressAreaFailedNoGroup')
        return
      }
      this.postcodeError = ''
      this.postalCode = res.postalCode
      this.setAddressValue(res.address)
      // isNewDistrict 는 '지역' 그룹이 없는 초안에서 시군구명 동반 여부를 가르는 값이라 함께 보관한다
      this.postcodeArea = { ...res.area, isNewDistrict: res.isNewDistrict }
      this.syncAreaSelection()
    },
    /** 주소 값은 실제 렌더된 입력 슬롯에 넣는다 — 확정 주소는 optional_fields, 미입력 주소는 input_fields */
    setAddressValue(address) {
      if (this.optionalFields.some(f => f.key === 'address')) this.optionalInputs.address = address
      else if (this.inputFields.some(f => f.key === 'address')) this.inputs.address = address
    },
    /** 해석된 지역을 드롭다운 선택에 반영 — 마스터에 없으면 임시 옵션을 만들어 그것을 선택한다 */
    syncAreaSelection() {
      if (!this.hasAreaGroup) return // 지역이 이미 확정된 초안 — commitPayload 가 직접 싣는다
      const items = (this.data.candidates || {})['지역'] || []
      const i = items.findIndex(c => this.chipCode(c) === this.postcodeAreaCode)
      if (i >= 0) {
        this.pendingAreaOption = null
        this.selected['지역'] = i
        return
      }
      // 마스터 미등재 시군구 — 임시 옵션은 누적이 아니라 **교체**다(두 번 검색해도 1개)
      this.pendingAreaOption = {
        label: `${this.postcodeArea.areaMainName} ${this.postcodeArea.areaSubName}`,
        code: this.postcodeAreaCode,
        areaSubName: this.postcodeArea.areaSubName
      }
      this.selected['지역'] = items.length // 임시 옵션은 displayCandidates 에서 맨 뒤에 붙는다
    },
    /** optional select 의 현재 code — 미선택 시 서버 default, 그것도 없으면 첫 항목 */
    optionalSelectCode(f) {
      const v = this.optionalSelected[f.key]
      if (v !== undefined) return String(v)
      if (f.default !== undefined && f.default !== null && f.default !== '') return String(f.default)
      return (f.items && f.items[0] && f.items[0].code != null) ? String(f.items[0].code) : ''
    },
    /** optional input 의 현재 값 — 미입력 시 서버 default */
    optionalInputVal(f) {
      const v = this.optionalInputs[f.key]
      if (v !== undefined) return v
      return (f.default !== undefined && f.default !== null) ? String(f.default) : ''
    },
    onOptionalSelect(key, val) {
      this.optionalSelected[key] = val
    },
    onOptionalInput(key, val) {
      this.optionalInputs[key] = val
    },
    isSelected(field, i) {
      const sel = this.selected[field]
      if (this.isMultiField(field)) return Array.isArray(sel) && sel.includes(i)
      return sel === i
    },
    onChipClick(field, i, c) {
      if (!this.groupHasCodes(field)) {
        this.$emit('refine', c.query) // 코드 없는 그룹 — 기존 즉시 재작성 경로
        return
      }
      if (this.isMultiField(field)) {
        const sel = this.selected[field] || []
        const idx = sel.indexOf(i)
        if (idx >= 0) sel.splice(idx, 1) // 재클릭 = 해당 항목만 해제
        else sel.push(i)
        if (sel.length) this.selected[field] = [...sel]
        else delete this.selected[field]
        return
      }
      const prev = this.selected[field]
      if (prev === i) delete this.selected[field] // 재클릭 = 해제
      else this.selected[field] = i
      // 증상 변경/해제 → 종속 확인사항 선택 리셋 (다른 유형의 후보로 교체됨)
      if (String(field).replace(/\s+/g, '') === '증상') {
        for (const mf of this.multiFieldSet) delete this.selected[mf]
      }
    }
  }
}
</script>

<style scoped>
.aw-draft {
  max-width: 100%;
  background: var(--color-primary-50, #E8EDF4);
  border: 1px solid var(--color-primary-100, #C5D1E3);
  border-radius: 12px;
  padding: 12px;
  white-space: normal;
}
.aw-draft-title { font-weight: 700; font-size: 14px; color: var(--color-primary-500, #3B5998); margin-bottom: 8px; }
.aw-draft-list { display: grid; grid-template-columns: auto 1fr; gap: 4px 10px; margin: 0 0 8px; font-size: 13px; }
.aw-draft-list dt { font-weight: 700; color: var(--color-gray-800, #202124); white-space: nowrap; }
.aw-draft-list dd { margin: 0; color: var(--color-gray-800, #202124); word-break: break-word; }
.aw-draft-warn {
  background: var(--color-warning-light, #F5EDDA);
  color: var(--color-warning, #C8870A);
  border: 1px solid var(--color-warning, #C8870A);
  border-radius: 8px; padding: 6px 8px; font-size: 12.5px; margin-bottom: 8px;
}
.aw-draft-status { font-size: 12.5px; color: var(--color-gray-600, #5f6368); margin-bottom: 6px; }
/* 유사 후보 선택 칩 — 보조 버튼(outline) 톤, 줄바꿈 허용 */
.aw-draft-candidates { margin-bottom: 8px; }
.aw-draft-optional { margin-bottom: 8px; }
.aw-draft-cand-hint { font-size: 12px; color: var(--color-gray-600, #5f6368); margin-bottom: 6px; }
.aw-draft-cand-group { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 6px; }
.aw-draft-cand-field { font-size: 12px; font-weight: 700; color: var(--color-gray-800, #202124); white-space: nowrap; }
.aw-draft-cand-chip {
  border: 1px solid var(--color-primary-500, #3B5998);
  background: var(--color-white, #fff);
  color: var(--color-primary-600, #2C4578);
  border-radius: var(--radius-full, 9999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.aw-draft-cand-chip:hover:not(:disabled) { background: var(--color-primary-50, #E8EDF4); }
.aw-draft-cand-chip:disabled { border-color: var(--color-gray-300, #b0bec5); color: var(--color-gray-400, #9aa0a6); cursor: not-allowed; }
/* 다중 선택 모드 — 선택된 칩은 filled 톤으로 반전 */
.aw-draft-cand-chip--selected { background: var(--color-primary-500, #3B5998); color: var(--color-white, #fff); }
.aw-draft-cand-chip--selected:hover:not(:disabled) { background: var(--color-primary-600, #2C4578); }
/* 전량 후보 드롭다운(지역) + 직접 입력 필드 — 칩과 동일한 톤 체계 */
.aw-draft-cand-select, .aw-draft-input {
  flex: 1 1 160px; min-width: 0; border: 1px solid var(--color-gray-300, #b0bec5);
  background: var(--color-white, #fff); color: var(--color-gray-800, #202124);
  border-radius: var(--radius-md, 8px); padding: 5px 8px; font-size: 12.5px;
}
.aw-draft-cand-select { border-color: var(--color-primary-500, #3B5998); }
.aw-draft-cand-select:focus, .aw-draft-input:focus { outline: none; border-color: var(--color-primary-500, #3B5998); }
.aw-draft-cand-select:disabled, .aw-draft-input:disabled {
  background: var(--color-gray-100, #eef0f3); border-color: var(--color-gray-300, #b0bec5); color: var(--color-gray-400, #9aa0a6);
}
.aw-draft-actions { display: flex; gap: 8px; }
.aw-draft-btn { border: none; border-radius: var(--radius-md, 8px); padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.aw-draft-commit { background: var(--color-primary-500, #3B5998); color: var(--color-white, #fff); }
.aw-draft-commit:hover:not(:disabled) { background: var(--color-primary-600, #2C4578); }
.aw-draft-cancel { background: var(--color-gray-100, #eef0f3); color: var(--color-gray-800, #202124); }
.aw-draft-cancel:hover:not(:disabled) { background: var(--color-gray-200, #e0e0e0); }
.aw-draft-btn:disabled { background: var(--color-gray-300, #b0bec5); color: var(--color-white, #fff); cursor: not-allowed; }
</style>
